import { ReactNode } from "react";
import ColumnHeaderFilter, {
  ColumnFilterValue,
} from "../Components/Common/UI/ColumnHeaderFilter";
import {
  ColumnFilterConfig,
  CUSTOM_COLUMN_ALLOWED_TYPES,
  STUDY_STATUS_OPTIONS,
} from "../Components/Common/UI/columnFilterTypes";
import { ColumnSortConfig } from "../Components/Common/UI/columnSortTypes";
import { CustomColumnDef } from "../Components/Common/UI/participantFilterFields/CustomColumnFilterField";
import { updateCustomHeaderValues } from "../Components/Common/UI/participantFilterFields/customColumnFilterUtils";
import { ParticipantsState } from "../Redux/reducers/participantsSlice";
import { isStudyColumnFilterActive } from "./participantFilterActive";

export const STUDY_SORTABLE_COLUMNS: Record<string, ColumnSortConfig> = {
  participantId: { sortKey: "subjectId", sortType: "text" },
  site: { sortKey: "siteName", sortType: "text" },
  status: { sortKey: "eligibilityStatus", sortType: "text" },
  screenerCompleted: { sortKey: "hasCompletedScreener", sortType: "boolean" },
  screenerReceived: { sortKey: "hasReceivedScreener", sortType: "boolean" },
  screenerCompletedDate: { sortKey: "screenerCompletedAt", sortType: "date" },
  groupCode: { sortKey: "groupCode", sortType: "text" },
  userGroup: { sortKey: "cohortName", sortType: "text" },
  randomization: { sortKey: "randomizationGroup", sortType: "text" },
  createdAt: { sortKey: "createdAt", sortType: "date" },
  lock: { sortKey: "isLocked", sortType: "boolean" },
};

export type StudyFilterContext = {
  filterState: Pick<
    ParticipantsState,
    | "siteIds"
    | "status"
    | "isLocked"
    | "screenerCompleted"
    | "screenerReceived"
    | "screenerCompletedFrom"
    | "screenerCompletedTo"
    | "cohortId"
    | "tagIds"
    | "notesQuery"
    | "customHeaderValues"
  >;
  sitesList: { id: string; name: string }[];
  cohortList: { id: string; label: string }[];
  tagList: { id: string; label: string }[];
  sortColumn: string;
  sortOrder: string;
  onSort: (sortKey: string, order: "ASC" | "DESC" | "") => void;
  onPatch: (patch: Partial<StudyFilterContext["filterState"]>) => void;
  onClearField: (field: string) => void;
};

const COLUMN_FILTER_CONFIGS: Record<
  string,
  Omit<ColumnFilterConfig, "options"> & {
    stateKey: string;
    optionsFromCtx?: (ctx: StudyFilterContext) => { label: string; value: string }[];
  }
> = {
  site: {
    filterKey: "siteIds",
    stateKey: "siteIds",
    type: "multiSelect",
    optionsFromCtx: (ctx) =>
      ctx.sitesList.map((s) => ({ label: s.name, value: s.id })),
  },
  status: {
    filterKey: "status",
    stateKey: "status",
    type: "multiSelect",
    optionsFromCtx: () => STUDY_STATUS_OPTIONS,
  },
  screenerCompleted: {
    filterKey: "screenerCompleted",
    stateKey: "screenerCompleted",
    type: "yesNo",
  },
  screenerReceived: {
    filterKey: "screenerReceived",
    stateKey: "screenerReceived",
    type: "yesNo",
  },
  screenerCompletedDate: {
    filterKey: "screenerCompletedDate",
    stateKey: "screenerCompletedDate",
    type: "dateRange",
  },
  userGroup: {
    filterKey: "cohortId",
    stateKey: "cohortId",
    type: "multiSelect",
    optionsFromCtx: (ctx) =>
      ctx.cohortList.map((c) => ({ label: c.label, value: c.id })),
  },
  tags: {
    filterKey: "tagIds",
    stateKey: "tagIds",
    type: "tagsAutocomplete",
  },
  lock: {
    filterKey: "isLocked",
    stateKey: "isLocked",
    type: "yesNo",
  },
  actions: {
    filterKey: "notesQuery",
    stateKey: "notesQuery",
    type: "text",
  },
};

function getCustomFilterType(
  questionType: string,
): ColumnFilterConfig["type"] | null {
  if (questionType === "number" || questionType === "calculated_field") {
    return "customNumber";
  }
  if (questionType === "date") return "customDate";
  if (["radio", "checkbox", "dropdown"].includes(questionType)) {
    return "customChoice";
  }
  if (questionType === "text" || questionType === "calculated_field_text") {
    return "customText";
  }
  return null;
}

function getFilterValue(
  def: (typeof COLUMN_FILTER_CONFIGS)[string],
  ctx: StudyFilterContext,
): ColumnFilterValue {
  if (def.type === "dateRange") {
    return {
      from: ctx.filterState.screenerCompletedFrom || "",
      to: ctx.filterState.screenerCompletedTo || "",
    };
  }
  if (def.type === "multiSelect" || def.type === "tagsAutocomplete") {
    return (
      (ctx.filterState[def.stateKey as keyof typeof ctx.filterState] as string[]) ||
      []
    );
  }
  return (
    (ctx.filterState[def.stateKey as keyof typeof ctx.filterState] as string) ||
    ""
  );
}

export function renderStudyColumnHeader(
  columnKey: string,
  label: string,
  ctx: StudyFilterContext | undefined,
  options?: {
    customColumn?: CustomColumnDef & { questionType: string };
  },
) {
  if (!ctx) {
    return label;
  }

  const sortConfig = STUDY_SORTABLE_COLUMNS[columnKey];

  if (columnKey.startsWith("custom:") && options?.customColumn) {
    const col = options.customColumn;
    const infoTooltip = col.questionLabel?.trim() || undefined;

    if (!CUSTOM_COLUMN_ALLOWED_TYPES.includes(col.questionType)) {
      return (
        <ColumnHeaderFilter label={label} infoTooltip={infoTooltip} />
      );
    }
    const filterType = getCustomFilterType(col.questionType);
    if (!filterType) {
      return (
        <ColumnHeaderFilter label={label} infoTooltip={infoTooltip} />
      );
    }

    const entry = ctx.filterState.customHeaderValues.find(
      (v) => v.key === col.varname,
    );
    const value: ColumnFilterValue = entry
      ? { key: col.varname, value: entry.value }
      : { key: col.varname, value: col.questionType === "number" ? "" : [] };

    return (
      <ColumnHeaderFilter
        label={label}
        infoTooltip={infoTooltip}
        filterActive={isStudyColumnFilterActive(
          columnKey,
          ctx.filterState,
          col.varname,
        )}
        filterConfig={{
          filterKey: col.varname,
          type: filterType,
          varname: col.varname,
          questionType: col.questionType,
        }}
        filterValue={value}
        customColumn={col}
        onFilterApply={(applied) => {
          const payload = applied as { key: string; value: string | string[] };
          const updated = updateCustomHeaderValues(
            payload.key,
            payload.value,
            ctx.filterState.customHeaderValues,
          );
          ctx.onPatch({ customHeaderValues: updated });
        }}
        onFilterClear={() => ctx.onClearField(`custom:${col.varname}`)}
      />
    );
  }

  const filterDef = COLUMN_FILTER_CONFIGS[columnKey];

  if (!filterDef && !sortConfig) {
    return label;
  }

  const headerLabel =
    columnKey === "actions" ? "Notes" : columnKey === "lock" ? "Lock" : label;

  let filterConfig: ColumnFilterConfig | undefined;
  let filterValue: ColumnFilterValue | undefined;
  let onFilterApply: ((value: ColumnFilterValue) => void) | undefined;
  let onFilterClear: (() => void) | undefined;
  let filterActive = false;

  if (filterDef) {
    filterConfig = {
      filterKey: filterDef.filterKey,
      type: filterDef.type,
      options: filterDef.optionsFromCtx?.(ctx),
    };
    filterValue = getFilterValue(filterDef, ctx);
    filterActive = isStudyColumnFilterActive(columnKey, ctx.filterState);

    onFilterApply = (applied: ColumnFilterValue) => {
      if (filterDef.type === "dateRange") {
        const range = applied as { from: string; to: string };
        ctx.onPatch({
          screenerCompletedFrom: range.from,
          screenerCompletedTo: range.to,
        });
        return;
      }
      ctx.onPatch({ [filterDef.stateKey]: applied } as Partial<
        StudyFilterContext["filterState"]
      >);
    };

    onFilterClear = () => {
      if (filterDef.type === "dateRange") {
        ctx.onClearField("screenerCompletedDate");
        return;
      }
      ctx.onClearField(filterDef.stateKey);
    };
  }

  return (
    <ColumnHeaderFilter
      label={headerLabel}
      filterActive={filterActive}
      filterConfig={filterConfig}
      filterValue={filterValue}
      onFilterApply={onFilterApply}
      onFilterClear={onFilterClear}
      sortConfig={sortConfig}
      sortColumn={ctx.sortColumn}
      sortOrder={ctx.sortOrder}
      onSort={ctx.onSort}
      tagOptions={ctx.tagList}
    />
  );
}
